import { useEffect, useState } from 'react';
import type { OrderedPage } from '../utils.js';

export function usePreviewLightbox(
  pagesByStudent: Record<string, OrderedPage[]>,
  fallbackUrls: Record<string, string>,
) {
  const [previewPage, setPreviewPage] = useState<{ bucketId: string; index: number } | null>(null);

  useEffect(() => {
    if (!previewPage) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewPage(null);
        return;
      }
      if (event.key === 'ArrowLeft') {
        setPreviewPage(prev => {
          if (!prev) return prev;
          return prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev;
        });
        return;
      }
      if (event.key === 'ArrowRight') {
        setPreviewPage(prev => {
          if (!prev) return prev;
          const pages = pagesByStudent[prev.bucketId] || [];
          return prev.index < pages.length - 1 ? { ...prev, index: prev.index + 1 } : prev;
        });
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [previewPage, pagesByStudent]);

  const previewPages = previewPage ? pagesByStudent[previewPage.bucketId] || [] : [];
  const activePreviewPage = previewPage ? previewPages[previewPage.index] : undefined;
  const activePreviewUrl = activePreviewPage
    ? activePreviewPage.url || fallbackUrls[activePreviewPage.imageId] || ''
    : '';

  const canPreviewPrev = Boolean(previewPage && previewPage.index > 0);
  const canPreviewNext = Boolean(previewPage && previewPage.index < previewPages.length - 1);

  const goToPreviewPrev = () => {
    setPreviewPage(prev => {
      if (!prev || prev.index <= 0) return prev;
      return { ...prev, index: prev.index - 1 };
    });
  };

  const goToPreviewNext = () => {
    setPreviewPage(prev => {
      if (!prev) return prev;
      const pages = pagesByStudent[prev.bucketId] || [];
      if (prev.index >= pages.length - 1) return prev;
      return { ...prev, index: prev.index + 1 };
    });
  };

  return {
    state: { previewPage, previewPages, activePreviewUrl, canPreviewPrev, canPreviewNext },
    actions: { setPreviewPage, goToPreviewPrev, goToPreviewNext },
  };
}
